@if (apiCompatibility.unsupported()) {
  <aside class="maintenance-notice" role="alert">
    {{ 'This app needs an update to work with the server.' | translate }}
    @if (isTauri) {
      {{ 'Please install the latest version of the app.' | translate }}
    } @else if (odinService.room) {
      {{ 'Your current meeting can continue. Reload after leaving the meeting.' | translate }}
    } @else {
      {{ 'Reload to update the app.' | translate }}
    }
  </aside>
}
@let showPanel = appStore.isConnectedView$ | async;
@let section = appStore.activeSection$ | async;
@let isConnected = odinService.status() === OdinConnectionStatus.connected;
@if (maintenance.active() && maintenance.noticeVisible()) {
  @if (odinService.room) {
    <aside class="maintenance-notice" role="status" aria-live="polite">
      <strong>{{ maintenance.title() | translate }}</strong>
      {{ maintenance.message() | translate }}
      <span>
        {{
          'Audio, video and chat remain available. Server features are temporarily paused.'
            | translate
        }}
      </span>
    </aside>
  } @else {
    <main class="maintenance-view" role="status">
      <span class="maintenance-logo" aria-hidden="true"></span>
      <h1>{{ maintenance.title() | translate }}</h1>
      <p>{{ maintenance.message() | translate }}</p>
      <p>{{ 'This page will reconnect automatically when the service is ready.' | translate }}</p>
    </main>
  }
}
@if (appReady() && !appService.startupError() && (!maintenance.active() || odinService.room)) {
  <div
    class="app-shell"
    [class.preparation-mode]="section === 'preparation-area' && !isConnected"
    [style.--app-background-image]="wallpaper()?.css">
    <img
      class="app-background"
      src="{{ wallpaper()?.src }}"
      crossorigin="anonymous"
      draggable="false"
      alt=""
      aria-hidden="true" />
    @if (appService.showSetupWizard()) {
      <andor-setup-wizard />
    } @else {
      <div
        class="app-stage {{ appStore.loginAreaLogoSize$ | async }} {{
          this.appStore.getConnectedClass$() | async
        }}">
        <div class="logo">
          <img
            src="{{ (appConfigStore.getAsset$(userSettings.colorMode + 'Logo') | async)?.url }}"
            crossorigin="anonymous" />
          @if (isTauri && odinService.status() === OdinConnectionStatus.disconnected) {
            <andor-square-icon-btn
              class="space-switch"
              [tooltip]="'Switch Space' | translate"
              (clicked)="spaceSheetOpen.set(true)">
              <fa-duotone-icon [icon]="faArrowRightArrowLeft"></fa-duotone-icon>
            </andor-square-icon-btn>
          }
        </div>
        <div class="app-nav">
          <andor-main-nav />
        </div>
        <div class="app-content">
          <andor-view-outlet />
        </div>
      </div>
    }
    @if (showPanel) {
      <div class="conferencing-controls">
        <andor-dock></andor-dock>
      </div>
    }
  </div>
  @if (
    ((appStore.panelSettingsExpanded$ | async) || (appStore.appInfoExpanded$ | async)) && showPanel
  ) {
    <div class="backdrop" (click)="closeMenus()"></div>
  }
}
@if (
  (!maintenance.active() && !appReady() && !appService.startupError()) ||
  (maintenance.active() && !maintenance.noticeVisible() && !odinService.room)
) {
  <div class="not-ready-background">
    <img class="bounce" src="assets/icon_dark.svg" />
    @if (appService.cooldown.remaining('startup')) {
      <p role="status">
        {{
          'Please wait [seconds] seconds before trying again.'
            | translate: { seconds: appService.cooldown.remaining('startup') }
        }}
      </p>
    }
  </div>
}

@if (!maintenance.active() && appService.startupError()) {
  <div class="not-ready-background startup-error">
    <img src="assets/icon_sad_dark.svg" />
    <div class="error-container">
      <andor-app-update-notice />
      <span>
        @switch (appService.startupError()) {
          @case ('configuration') {
            {{
              'The saved app configuration is incomplete or invalid. Please contact the app administrator to repair it.'
                | translate
            }}
          }
          @case ('not-set-up') {
            {{
              'This app is not set up yet. Please ask its administrator to complete the setup in a web browser.'
                | translate
            }}
          }
          @case ('invalid-response') {
            {{
              'The server returned invalid startup data. Please retry or contact the app administrator.'
                | translate
            }}
          }
          @default {
            {{
              'Unable to connect to the Rooms backend services. Please try again in a few minutes. If the issue persists, feel free to contact us on Discord or via email at:'
                | translate
            }}
            <a href="mailto:odin@4players.io">{{ 'odin@4players.io' }}</a>
          }
        }
      </span>
      <div class="actions">
        <a href="https://4np.de/discord" target="_blank">
          <button class="support">{{ 'Join Discord Community' | translate }}</button>
        </a>
        <a>
          <button class="retry" (click)="init()">{{ 'Retry' | translate }}</button>
        </a>
        @if (isTauri) {
          <a>
            <button class="retry" (click)="spaceSheetOpen.set(true)">
              {{ 'Switch Space' | translate }}
            </button>
          </a>
        }
      </div>
    </div>
  </div>
}

@if (permissions.showPermissionTutorial()) {
  <andor-permission-tutorial
    [cameraDenied]="permissions.cameraAccess() === 'denied'"
    [microphoneDenied]="permissions.microphoneAccess() === 'denied'"
    (dismissed)="permissions.dismissPermissionTutorial()" />
}

@if (appReady() && odinService.status() === OdinConnectionStatus.reconnecting) {
  <andor-reconnecting-overlay
    [colorMode]="userSettings.colorMode ?? ''"></andor-reconnecting-overlay>
}

@if (appReady()) {
  @if (appService.appType()?.showBranding) {
    <andor-promo-button [colorMode]="userSettings.colorMode" />
  }
}

@if (appReady() && (odinService.odinRoom$ | async)) {
  <div class="app-info {{ (appStore.appInfoExpanded$ | async) ? 'expanded' : '' }}">
    @if (appStore.appInfoExpanded$ | async) {
      <div class="header">
        <h3>{{ 'Connection Information' | translate }}</h3>
        <andor-icon-btn (click)="appStore.update({ appInfoExpanded: false })">
          <fa-duotone-icon [icon]="faClose"></fa-duotone-icon>
        </andor-icon-btn>
      </div>
      <div class="content">
        <andor-connection-overview></andor-connection-overview>
        <div class="actions">
          <andor-button color="neutral" (click)="copyDiagnostics()">
            <fa-icon class="copy-icon" [icon]="faCopy"></fa-icon>
            <span>{{ (diagnosticsCopied() ? 'Copied!' : 'Copy Diagnostics') | translate }}</span>
          </andor-button>
          @if (config.showFeedbackButton) {
            <andor-button
              (click)="sendReport()"
              [disabled]="maintenance.active() || submittingReport()">
              <span>{{ 'Send Report' | translate }}</span>
            </andor-button>
          }
        </div>
        <p class="app-version">{{ 'Version' | translate }} {{ appService.version }}</p>
      </div>
    } @else {
      @let problems = streamHealth.problemCount();
      <button
        class="connection-tab"
        [attr.aria-label]="'Connection Information' | translate"
        (click)="appStore.update({ appInfoExpanded: true })">
        <andor-ping-indicator-tiny></andor-ping-indicator-tiny>
        @if (problems) {
          <span
            class="connection-tab__problems"
            [attr.aria-label]="'Streams with problems: [count]' | translate: { count: problems }">
            {{ problems }}
          </span>
        }
      </button>
    }
  </div>
}

@if (streamStatsDialog.request(); as statsRequest) {
  <andor-stream-stats
    [userId]="statsRequest.userId"
    [initialKind]="statsRequest.kind"
    (closed)="streamStatsDialog.close()" />
}

@if (spaceSheetOpen()) {
  <andor-space-sheet [required]="spaceRequired" (closed)="spaceSheetOpen.set(false)" />
}
